import { useState, useEffect } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader, FooterBar } from './Stepper.jsx';
import { searchTuss } from '../api/labs.js';

export default function Step2Confirm({ state, setState, onNext, onBack }) {
  const [exams, setExams] = useState(
    state.exams?.map(e => ({ ...e, fromOrder: e.fromOrder ?? true })) || []
  );
  // Um pedido (Order) pode ter N pedidos médicos (Prescription) — 1 upload = 1 pedido
  // médico = 1 médico. Cada um precisa da própria revisão, não só o primeiro upload.
  // Campos podem vir null do OCR — inputs controlados exigem string.
  const [doctors, setDoctors] = useState(
    (state.uploads || []).map(u => ({
      name: u.doctor?.name || '',
      crm: u.doctor?.crm || '',
      specialty: u.doctor?.specialty || '',
      date: u.doctor?.date || '',
      noDate: false,
    }))
  );
  const updateDoctor = (i, patch) => setDoctors(ds => ds.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  // "Sem Data" é a única exceção de campo obrigatório: desliga a validade do
  // pedido em vez de exigir uma data que o pedido médico pode não ter.
  const toggleNoDate = (i) => setDoctors(ds => ds.map((d, idx) => idx === i ? { ...d, noDate: !d.noDate, date: d.noDate ? d.date : '' } : d));
  // Nome, CRM e Data (ou "Sem Data") são obrigatórios; Especialidade é o único opcional.
  const doctorsValid = doctors.every(d => d.name.trim() && d.crm.trim() && (d.noDate || d.date));
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Busca real no catálogo TUSS, com debounce
  useEffect(() => {
    const q = catalogQuery.trim();
    if (q.length < 2) { setCatalogResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      searchTuss(q)
        .then(data => setCatalogResults(data || []))
        .catch(() => setCatalogResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [catalogQuery]);

  const toggle = (e) => {
    const exists = exams.find(x => x.code === e.code);
    if (exists) setExams(exams.filter(x => x.code !== e.code));
    else setExams([...exams, { code: e.code, name: e.name, fromOrder: false, note: 'Adicionado manualmente' }]);
  };
  const remove = (exam) => setExams(exams.filter(x => x !== exam));
  const isUnavailable = (e) => e.available === false;
  const availableExams = exams.filter(e => !isUnavailable(e));
  // Não oferecidos ficam só na revisão — não seguem para o pedido.
  const save = () => {
    setState(s => {
      const uploads = (s.uploads || []).map((u, i) => ({ ...u, doctor: { ...u.doctor, ...doctors[i] } }));
      return { ...s, uploads, exams: availableExams };
    });
    onNext();
  };

  return (
    <>
      <MobHeader title="Confirmar exames" onBack={onBack}/>
      <div style={{ flex: 1, minHeight: 0, padding: '18px 16px', overflowY: 'auto', background: 'var(--bg)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>Revise os dados</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>Exames adicionados fora do pedido não poderão ser realizados.</p>

        <div className="card" style={{ padding: 14, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Exames ({exams.length})</h3>
            <button onClick={() => setShowCatalog(!showCatalog)} style={{ color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600, display: 'flex', gap: 4, alignItems: 'center' }}>
              <Icon.plus size={14} color="var(--brand-primary)"/> {showCatalog ? 'Fechar' : 'Adicionar'}
            </button>
          </div>

          {showCatalog && (
            <div style={{ marginBottom: 14, padding: 12, background: 'var(--marble-soft)', borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--warning-darker)', display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 10 }}>
                <Icon.info size={14} color="var(--warning-dark)" style={{ flexShrink: 0, marginTop: 1 }}/>
                <span>Adicionais não estão no pedido e precisarão de nova autorização médica.</span>
              </div>
              <input
                className="input"
                autoFocus
                placeholder="Busque um exame (ex.: ferritina, vitamina D...)"
                value={catalogQuery}
                onChange={e => setCatalogQuery(e.target.value)}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {searching && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Buscando…</div>}
                {!searching && catalogQuery.trim().length >= 2 && catalogResults.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Nenhum exame encontrado no catálogo.</div>
                )}
                {catalogResults.map(e => {
                  const added = exams.find(x => x.code === e.code);
                  return (
                    <button key={e.code} onClick={() => toggle(e)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, background: added ? 'var(--brand-primary-soft)' : 'var(--white)', border: '1px solid ' + (added ? 'var(--brand-primary)' : 'var(--border)'), textAlign: 'left' }}>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>{e.name}</span>
                      <span style={{ color: added ? 'var(--brand-primary)' : 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>{added ? 'Remover' : 'Adicionar'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {exams.map((e, i) => {
              const off = isUnavailable(e);
              return (
                <div key={e.code || `na-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 10, border: off ? '1px dashed var(--border-strong)' : '1px solid var(--border)', borderRadius: 10, background: off ? 'var(--marble-soft)' : e.fromOrder ? 'var(--white)' : 'var(--warning-tint-2)', opacity: off ? 0.75 : 1 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: off ? 'var(--border)' : e.fromOrder ? 'var(--brand-primary-soft)' : 'var(--overlay-warning-20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon.flask size={16} color={off ? 'var(--text-muted)' : e.fromOrder ? 'var(--brand-primary)' : 'var(--warning-dark)'}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, lineHeight: 1.25, color: off ? 'var(--text-muted)' : 'var(--midnight)' }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{off ? 'Lido no pedido, mas indisponível nos laboratórios parceiros' : e.note}</div>
                  </div>
                  {off && <span className="chip" style={{ background: 'var(--border)', color: 'var(--text-muted)', flexShrink: 0 }}>Não oferecido</span>}
                  {!off && !e.fromOrder && <span className="chip" style={{ background: 'var(--overlay-warning-25)', color: 'var(--warning-darker)' }}>Add</span>}
                  <button onClick={() => remove(e)} style={{ color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
                    <Icon.x size={16}/>
                  </button>
                </div>
              );
            })}
          </div>

        </div>

        {doctors.map((doctor, i) => (
          <div key={state.uploads[i]?.id || i} className="card" style={{ padding: 14, marginTop: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              Dados do médico{doctors.length > 1 ? ` — pedido ${i + 1} de ${doctors.length}` : ''}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label className="input-label">Nome</label>
                <input className="input" value={doctor.name} onChange={e => updateDoctor(i, { name: e.target.value })}/>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label className="input-label">CRM</label>
                  <input className="input" value={doctor.crm} onChange={e => updateDoctor(i, { crm: e.target.value })}/>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="input-label">Data</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" checked={doctor.noDate} onChange={() => toggleNoDate(i)} style={{ width: 13, height: 13 }}/>
                      Sem Data
                    </label>
                  </div>
                  <input className="input" type="date" value={doctor.date} disabled={doctor.noDate}
                    onChange={e => updateDoctor(i, { date: e.target.value })}
                    style={doctor.noDate ? { opacity: 0.5 } : undefined}/>
                </div>
              </div>
              <div>
                <label className="input-label">Especialidade</label>
                <input className="input" value={doctor.specialty} onChange={e => updateDoctor(i, { specialty: e.target.value })}/>
              </div>
            </div>
            {(() => {
              if (doctor.noDate) {
                return (
                  <div style={{ marginTop: 12, padding: 10, background: 'var(--primary-tint)', borderRadius: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <Icon.info size={14} color="var(--brand-primary)" style={{ marginTop: 2, flexShrink: 0 }}/>
                    <div style={{ fontSize: 12, color: 'var(--brand-primary)' }}>
                      Pedidos médicos sem data podem ser recusados pelo laboratório.
                    </div>
                  </div>
                );
              }
              // Validade real: 30 dias a partir da data lida do pedido
              if (!doctor.date) return null;
              const ageDays = Math.floor((Date.now() - new Date(doctor.date + 'T00:00:00')) / 86400000);
              if (Number.isNaN(ageDays)) return null;
              const valid = ageDays >= 0 && ageDays <= 30;
              return (
                <div style={{ marginTop: 12, padding: 10, background: valid ? 'var(--green-soft)' : 'var(--primary-tint)', borderRadius: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  {valid
                    ? <Icon.checkCircle size={14} color="var(--green-mid)" style={{ marginTop: 2, flexShrink: 0 }}/>
                    : <Icon.info size={14} color="var(--brand-primary)" style={{ marginTop: 2, flexShrink: 0 }}/>}
                  <div style={{ fontSize: 12, color: valid ? 'var(--green-dark-2)' : 'var(--brand-primary)' }}>
                    {valid
                      ? `Pedido dentro da validade de 30 dias (emitido há ${ageDays} dia${ageDays === 1 ? '' : 's'})`
                      : ageDays > 30
                        ? `Pedido emitido há ${ageDays} dias — fora da validade de 30 dias. O laboratório pode recusá-lo.`
                        : 'A data do pedido parece estar no futuro — confira o campo Data.'}
                  </div>
                </div>
              );
            })()}
          </div>
        ))}
      </div>
      <FooterBar onBack={onBack} onNext={save} canNext={availableExams.length > 0 && doctorsValid}
        hint={
          exams.length > 0 && availableExams.length === 0
            ? 'Nenhum dos exames lidos é oferecido pelos laboratórios parceiros'
            : !doctorsValid
              ? 'Preencha nome, CRM e data (ou marque "Sem Data") de todos os médicos'
              : null
        }/>
    </>
  );
}
